// src/pages/users/UserUpdatePage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../styles/Buttons.css';
import '../../components/DataTable.css';
import '../orgs/OrgUpdatePage.css'; // reuse the same card + input styles
import { SERVER_URL } from '../../constants/server-type';

export default function UserUpdatePage() {
  const { id } = useParams();               // "add" or an _id
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const orgFromQuery = params.get('org') || '';

  const isAdd = !id || id === 'add';

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    workPhone: '',
    mobilePhone: '',
    organization: '',
    password: ''
  });

  const [passwordValidations, setPasswordValidations] = useState({
    minLength: false, hasUppercase: false, hasLowercase: false, hasNumber: false, hasSymbol: false, isValid: false
  });

  // Prefill organization on Add (from ?org=...) and fetch on Edit
  useEffect(() => {
    if (isAdd) {
      setUserData(prev => ({ ...prev, organization: orgFromQuery }));
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${SERVER_URL}/users/getById/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        });
        const data = await r.json();
        setUserData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          role: data.role || '',
          email: data.email || '',
          workPhone: data.workPhone || '',
          mobilePhone: data.mobilePhone || '',
          organization: data.organization || orgFromQuery || '',
          password: ''
        });
      } catch (e) {
        console.error(e);
        alert('Unable to load user');
      }
    })();
  }, [id, isAdd, orgFromQuery]);

  const validatePassword = (password) => {
    const minLength = password.length >= 7;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    return { minLength, hasUppercase, hasLowercase, hasNumber, hasSymbol,
      isValid: minLength && hasUppercase && hasLowercase && hasNumber && hasSymbol };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setUserData(prev => ({ ...prev, password: value }));
    setPasswordValidations(validatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const method = isAdd ? 'POST' : 'PUT';
    const url = isAdd ? 'SERVER_URL/users/createDoc' : `SERVER_URL/users/updateDoc/${id}`;

    const payload = { ...userData, isVerified: true };
    if (!payload.password) delete payload.password;

    try {
      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) throw new Error('Save failed');
      navigate('/user-management'); // or back to the org page if you prefer
    } catch (err) {
      console.error(err);
      alert('Error saving user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="datatable-container" style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="datatable-header">
        <h3 style={{ margin: 0 }}>{isAdd ? 'Add User' : 'Edit User'}</h3>
      </div>

      <form onSubmit={handleSubmit} className="org-form">
        {/* User Details */}
        <div className="org-card">
          <div className="org-card-header"><h4>User Details</h4></div>
          <div className="org-card-body">
            <div className="form-grid">
              <div className="form-row">
                <label>First Name</label>
                <input className="dt-input" type="text" name="firstName" autoComplete="off"
                  value={userData.firstName} onChange={handleChange} required />
              </div>
              <div className="form-row">
                <label>Last Name</label>
                <input className="dt-input" type="text" name="lastName" autoComplete="off"
                  value={userData.lastName} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <label>Email</label>
              <input className="dt-input" type="email" name="email" autoComplete="off"
                value={userData.email} onChange={handleChange} required />
            </div>

            <div className="form-grid">
              <div className="form-row">
                <label>Work Phone</label>
                <input className="dt-input" type="text" name="workPhone" autoComplete="off"
                  value={userData.workPhone} onChange={handleChange} />
              </div>
              <div className="form-row">
                <label>Mobile Phone</label>
                <input className="dt-input" type="text" name="mobilePhone" autoComplete="off"
                  value={userData.mobilePhone} onChange={handleChange} />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-row">
                <label>Role</label>
                <select className="dt-input" name="role" value={userData.role}
                        onChange={handleChange} required>
                  <option value="">Select Role</option>
                  <option value="Admin">Admin</option>
                  <option value="Manager">Manager</option>
                  <option value="User">User</option>
                </select>
              </div>
              <div className="form-row">
                <label>Organization</label>
                <input className="dt-input" type="text" name="organization" autoComplete="off"
                  value={userData.organization} onChange={handleChange}
                  readOnly={!!orgFromQuery}  /* lock when coming from Users tab */
                />
              </div>
            </div>
          </div>
        </div>

        {/* Password (optional) */}
        <div className="org-card">
          <div className="org-card-header"><h4>Password</h4></div>
          <div className="org-card-body">
            <div className="form-grid">
              <div className="form-row">
                <label>{isAdd ? 'Password' : 'New Password'} (optional)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="dt-input"
                    style={{ flex: 1 }}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    value={userData.password}
                    onChange={handlePasswordChange}
                    placeholder={isAdd ? 'Set an initial password' : 'Leave blank to keep unchanged'}
                  />
                  <button
                    type="button"
                    className="btn-gray"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {userData.password ? (
                <div className="form-row">
                  <label>Password Requirements</label>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    <li style={{ color: passwordValidations.minLength ? 'green' : 'red' }}>Minimum 7 characters</li>
                    <li style={{ color: passwordValidations.hasUppercase ? 'green' : 'red' }}>At least 1 uppercase letter</li>
                    <li style={{ color: passwordValidations.hasLowercase ? 'green' : 'red' }}>At least 1 lowercase letter</li>
                    <li style={{ color: passwordValidations.hasNumber ? 'green' : 'red' }}>At least 1 number</li>
                    <li style={{ color: passwordValidations.hasSymbol ? 'green' : 'red' }}>At least 1 symbol (e.g., !@#$)</li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="datatable-footer" style={{ marginTop: 12 }}>
          <div className="datatable-controls">
            <button
              type="submit"
              className="btn-green"
              disabled={loading || (userData.password && !passwordValidations.isValid)}
            >
              {loading ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn-gray" onClick={() => navigate(-1)} style={{ marginLeft: 8 }}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
